import { useEffect, useRef } from 'react'
import { Dropdown, useDrawer } from '@iveco-ti/iveco-ds'
import {
  LoaderCircle,
  OctagonAlert,
  Pencil,
  Pin,
  PinOff,
  Share2,
  Trash,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useSwiper } from 'swiper/react'

import { Toast } from '@/common/components/Toast'
import { ROUTES } from '@/common/constants/routes'
import { PostModel } from '@/common/models/post'
import { useExcludePost } from '../hooks/useExcludePost'
import { useTogglePinPost } from '../hooks/useTogglePinPost'
import { useViewNews } from '../hooks/useViewNews'
import { useEditNewsStore } from '../stores/useEditNewsStore'

type NewsHighlightedProps = {
  post: PostModel
}

export function NewsHighlighted({ post }: NewsHighlightedProps) {
  const { t } = useTranslation('panel')
  const ref = useRef<HTMLDivElement | null>(null)
  const swiper = useSwiper()
  const { viewNews } = useViewNews()
  const { handleTogglePinPost, isTogglingPinPost } = useTogglePinPost()
  const { handleExcludePost, isExcludingPost } = useExcludePost()
  const { setNews } = useEditNewsStore()
  const drawerEditNews = useDrawer('drawer-edit-news')

  const handleCopySharableLink = async () => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}${ROUTES.PANEL.NEWS}?id=${post.id}`,
      )

      toast(Toast, {
        data: {
          type: 'info',
          icon: Share2,
          description: t('news.card_news.copied_link_toast.success.title'),
        },
      })
    } catch {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: OctagonAlert,
          description: t('news.card_news.copied_link_toast.error.title'),
        },
      })
    }
  }

  const handleEditNews = () => {
    setNews(post)
    drawerEditNews.open()
  }

  const IS_PINNED = post?.PostsPinned?.length > 0

  useEffect(() => {
    if (ref.current && swiper) {
      ref.current.addEventListener('mouseenter', () => {
        swiper?.autoplay?.stop()
      })

      ref.current.addEventListener('mouseleave', () => {
        swiper?.autoplay?.start()
      })
    }
  }, [swiper, ref])

  return (
    <div className="relative">
      <div
        className="w-full h-52 rounded-t-2xl cursor-pointer"
        ref={ref}
        onClick={() => viewNews(post)}
      >
        <img
          src={post.postImage || ''}
          alt={post.title || post.subTitle || ''}
          className="object-cover h-full w-full rounded-t-2xl"
        />

        <h4 className="text-4xl font-bold text-base-100 absolute top-8 left-8 text-shadow-lg">
          {post.title}
        </h4>

        <h5 className="text-lg text-base-300 font-semibold absolute top-20 left-8 text-shadow-md">
          {post.subTitle}
        </h5>
      </div>

      <div className="absolute top-4 right-6 bg-white rounded-md">
        <Dropdown.Root>
          <Dropdown.Trigger className="!text-brand-700" />

          <Dropdown.Body>
            <Dropdown.List>
              <Dropdown.Item
                className="text-brand-900"
                onClick={handleCopySharableLink}
              >
                <Dropdown.Icon icon={<Share2 size={16} />} />
                {t('news.card_news.share_button.tooltip')}
              </Dropdown.Item>

              <Dropdown.Item
                className="text-brand-900"
                onClick={() => handleTogglePinPost(post.id)}
              >
                {IS_PINNED && !isTogglingPinPost && (
                  <PinOff className="size-4 text-brand-900" />
                )}

                {!IS_PINNED && !isTogglingPinPost && (
                  <Pin className="size-4 text-brand-900" />
                )}

                {isTogglingPinPost && (
                  <LoaderCircle className="size-4 text-brand-900 animate-spin" />
                )}

                {IS_PINNED
                  ? t('news.card_news.unpin_button.tooltip')
                  : t('news.card_news.pin_button.tooltip')}
              </Dropdown.Item>

              <Dropdown.Item
                className="text-brand-900"
                onClick={handleEditNews}
              >
                <Dropdown.Icon icon={<Pencil size={16} />} />
                {t('news.card_news.edit_button.label')}
              </Dropdown.Item>

              <Dropdown.Item
                className="text-danger-900"
                onClick={() => handleExcludePost(post.id)}
              >
                {isExcludingPost ? (
                  <LoaderCircle className="size-4 text-danger-900 animate-spin" />
                ) : (
                  <Trash className="size-4 text-danger-900" />
                )}

                {t('news.card_news.delete_button.label')}
              </Dropdown.Item>
            </Dropdown.List>
          </Dropdown.Body>
        </Dropdown.Root>
      </div>
    </div>
  )
}
